import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeft } from '@phosphor-icons/react'
import CardWrapper from './card-wrapper'
import { Alert, AlertProps, Button, message, Modal, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { ContainerScrollProducts, ContainerSingleSelect, containerBody, ContainerScroll } from '../styles'
import { CardStopProps, ItemStop } from './card-stop'
import TitleStop from './title-stop'
import SingleSelect from '../../../../../../shared/single-select'
import { Store } from '../../../../asset-management/domain/store'
import { getStores } from '../../../../../../domain/resource-management/resource-management.services'
import { ITEM_STATES, JobItem } from '../../../domain/items'
import ListProducts from './list-products'
import { IProduct } from './product'
import { postMultistopUseCase } from '../../../application/post-multistop.use-case'
import { odinNebulaMultistopRepository } from '../../odin-nebula-multistop.repository'
import ListProductsWithDropdown from './list-products-with-dropdown'
import { patchMoveItemsStopUseCase } from '../../../application/patch-move-items-stop.use-case'
import { DURATION_MESSAGES_IN_SEC } from '../constants'
import { StepState } from '../../../domain/task'

interface Message {
  message: string | undefined
  description?: string
  type?: AlertProps['type']
}
interface FormStopProps {
  jobUuid: string
  stops: CardStopProps[]
  items: JobItem[]
  currentStop?: CardStopProps
  hanleCancel: () => void
  hanleSave: () => void
}

const FormStop: FC<FormStopProps> = ({ jobUuid, stops, items, currentStop, hanleCancel, hanleSave }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [needReload, setNeedReload] = useState(false)
  const [openSelectProducts, setOpenSelectProducts] = useState(false)
  const [currentStore, setCurrentStore] = useState<Store | undefined>()
  const [storesList, setStoresList] = useState<Store[]>([])
  const [numberOfPages, setNumberOfPages] = useState<number>(0)
  const [alertForm, setAlertForm] = useState<Message | undefined>()
  const [localStops, setLocalStops] = useState<CardStopProps[]>(stops)
  const [productsSelected, setProductsSelected] = useState<IProduct[]>([])
  const numberNextStop = useMemo(() => localStops.filter((stop) => !stop.isOrigin).length + 1, [stops])

  useEffect(() => {
    if (currentStop && storesList.length) {
      onChangeSelectedStore(currentStop.id)
    }
  }, [storesList])

  useEffect(() => {
    if (currentStop) {
      const productsEdit = currentStop.items.map((item) => buildProductFromItems(item)).filter((item) => item !== null)
      setProductsSelected((_) => productsEdit)
    }
  }, [currentStop])

  const handleBackButton = () => {
    if (!needReload) {
      hanleCancel()
      return
    }
    hanleSave()
  }

  const getProductsInStoresOrigin = () => {
    return localStops.filter((store) => store.isOrigin).flatMap((store) => store.items)
  }

  const getStoreOrigin = () => {
    return localStops.filter((store) => store.isOrigin)[0]
  }

  const buildProductFromItems = (item: ItemStop): IProduct | null => {
    const findItem = items.find((i) => i.itemId === item.id)
    if (findItem) {
      const product: IProduct = {
        id: findItem.itemId,
        name: `${findItem.name} - ${findItem.presentationV2.quantity} ${findItem.presentationV2.unit}`,
        description: `SKU: ${findItem.attributes.sku}`,
        photo: findItem.photoUrl,
        disabled: findItem.state !== ITEM_STATES.PENDING
      }

      return product
    }

    return null
  }

  const products: IProduct[] = useMemo(() => {
    return getProductsInStoresOrigin()
      .map((item) => buildProductFromItems(item))
      .filter((item) => item !== null)
  }, [items, localStops])

  const onPageChange = (current, pageSize) => {
    getStoreList({ limit: pageSize, offset: current * pageSize - pageSize })
  }

  const onSearchStore = (limit, storeName) => {
    getStoreList({ limit: limit, offset: 0, name: storeName })
  }

  const getStoreList = async (storeFilters) => {
    const stores = await getStores(storeFilters)
    setStoresList(stores.stores)
    setNumberOfPages(stores.numberOfPages)
  }

  const onChangeSelectedStore = useCallback(
    (storeId: string) => {
      const newStoreSelected = storesList.find(({ id }) => id === storeId)
      if (newStoreSelected) setCurrentStore((_) => newStoreSelected)
    },
    [storesList]
  )

  const removeProductFromProductsSelected = (productId: string) => {
    setProductsSelected((prevState) => prevState.filter((product) => product.id !== productId))
  }

  const addProductFromProductsSelected = (productId: string) => {
    const product = buildProductFromItems({ sku: '', id: productId })
    if (!product) return

    setProductsSelected((prevState) => [product, ...prevState])
  }

  const removeProductFromStop = (itemId: string) => {
    setLocalStops((prevState) =>
      prevState.map((stop) => ({
        ...stop,
        items: stop.items.filter((item) => item.id !== itemId)
      }))
    )
  }

  const addProductToOrigin = useCallback(
    (itemId: string) => {
      setLocalStops((prevState) => {
        const findItem = items.find((item) => item.itemId === itemId)
        if (!findItem) return prevState

        const itemToAdd: ItemStop = { sku: findItem.attributes.sku, id: findItem.itemId }

        return prevState.map((stop) => {
          if (stop.isOrigin && !stop.items.some((item) => item.id === itemId)) {
            return {
              ...stop,
              items: [...stop.items, itemToAdd]
            }
          }
          return stop
        })
      })
    },
    [items]
  )

  const handleAddProduct = (itemId: string) => {
    if (!itemId) return

    setOpenSelectProducts(false)
    if (getProductsInStoresOrigin().length === 1) {
      setAlertForm({ description: t('multistop.at-least-one-product'), type: 'error', message: undefined })
      return
    }

    if (!currentStop) {
      addProductFromProductsSelected(itemId)
      removeProductFromStop(itemId)
    } else {
      confirmAddProduct(currentStop.id, itemId)
    }
  }

  const handleDeleteProduct = (itemId: string) => {
    if (!itemId) return

    if (!currentStop) {
      removeProductFromProductsSelected(itemId)
      addProductToOrigin(itemId)
    } else {
      const storeOrigin = getStoreOrigin()
      if (storeOrigin) {
        confirmDeleteProduct(storeOrigin.id, itemId)
      }
    }
  }

  const createStop = async () => {
    setLoading(true)

    try {
      await postMultistopUseCase(odinNebulaMultistopRepository)(jobUuid, {
        store_id: currentStore.id,
        item_ids: productsSelected.map((product) => product.id)
      })
      message.success(t('multistop.success-create'), DURATION_MESSAGES_IN_SEC)
      hanleSave()
    } catch (error) {
      message.error(t('multistop.error-create'), DURATION_MESSAGES_IN_SEC)
    } finally {
      setLoading(false)
    }
  }

  const hanleMoveProductBetweenStops = async (storeId: string, productId: string): Promise<boolean> => {
    try {
      await patchMoveItemsStopUseCase(odinNebulaMultistopRepository)(jobUuid, {
        item_ids: [productId],
        destination_store: storeId
      })
      message.success(t('multistop.success-move-product'), DURATION_MESSAGES_IN_SEC)
      return true
    } catch (error) {
      message.error(t('multistop.error-move-product'), DURATION_MESSAGES_IN_SEC)
      return false
    } finally {
      setNeedReload(true)
    }
  }

  const confirmMoveProduct = (storeId: string, productId: string) => {
    const product = items.find((item) => item.itemId === productId)
    const stop = localStops.find(({ id }) => id === storeId)

    const modal = Modal.confirm({
      title: t('multistop.confirm-move-product.title'),
      content: (
        <div style={{ display: 'flex', gap: '20px', flexDirection: 'column' }}>
          <span>
            {t('multistop.confirm-move-product.message', {
              nameProduct: product?.name,
              nameStop: stop?.title,
              nameStore: stop?.storeName
            })}
          </span>
          <span>{productsSelected.length === 1 && t('multistop.empty-product-message')}</span>
        </div>
      ),
      okText: t('multistop.confirm-move-product.confirm'),
      cancelText: t('multistop.confirm-move-product.cancel'),
      zIndex: 10001002,
      onOk: async () => {
        modal.update({ okButtonProps: { loading: true } })
        const result = await hanleMoveProductBetweenStops(storeId, productId)
        if (result) {
          if (productsSelected.length === 1) {
            handleBackButton()
            return
          }
          removeProductFromStop(productId)
          removeProductFromProductsSelected(productId)
          if (stop.isOrigin) addProductToOrigin(productId)
        }
        modal.update({ okButtonProps: { loading: false } })
      }
    })
  }

  const confirmAddProduct = (storeId: string, productId: string) => {
    const product = items.find((item) => item.itemId === productId)
    const stop = localStops.find(({ id }) => id === storeId)

    const modal = Modal.confirm({
      title: t('multistop.confirm-add-product.title'),
      content: t('multistop.confirm-add-product.message', {
        nameProduct: product?.name,
        nameStop: stop?.title,
        nameStore: stop?.storeName
      }),
      okText: t('multistop.confirm-add-product.confirm'),
      cancelText: t('multistop.confirm-add-product.cancel'),
      zIndex: 10001002,
      onOk: async () => {
        modal.update({ okButtonProps: { loading: true } })
        const result = await hanleMoveProductBetweenStops(storeId, productId)
        if (result) {
          removeProductFromStop(productId)
          addProductFromProductsSelected(productId)
        }
        modal.update({ okButtonProps: { loading: false } })
      }
    })
  }

  const confirmDeleteProduct = (storeId: string, productId: string) => {
    const modal = Modal.confirm({
      title: t('multistop.confirm-delete-product.title'),
      content: (
        <div style={{ display: 'flex', gap: '20px', flexDirection: 'column' }}>
          <span>{t('multistop.confirm-delete-product.message')}</span>
          <span>{productsSelected.length === 1 && t('multistop.empty-product-message')}</span>
        </div>
      ),
      okText: t('multistop.confirm-delete-product.confirm'),
      cancelText: t('multistop.confirm-delete-product.cancel'),
      zIndex: 10001002,
      onOk: async () => {
        modal.update({ okButtonProps: { loading: true } })
        const result = await hanleMoveProductBetweenStops(storeId, productId)
        if (result) {
          if (productsSelected.length === 1) {
            handleBackButton()
            return
          }
          removeProductFromStop(productId)
          removeProductFromProductsSelected(productId)
          addProductToOrigin(productId)
        }
        modal.update({ okButtonProps: { loading: false } })
      }
    })
  }

  const getStoresDropdownChangeStop = useCallback(() => {
    return localStops
      .filter(({ id, status }) => id !== currentStop.id && status === StepState.PENDING)
      .map((stop) => ({ id: stop.id, name: stop.title }))
  }, [localStops, currentStop])

  return (
    <CardWrapper
      style={{ ...containerBody, display: 'flex', gap: '16px', flexDirection: 'column', maxHeight: '480px' }}
    >
      {alertForm && <Alert {...alertForm} showIcon closable onClose={() => setAlertForm(undefined)} />}

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button
          icon={<ArrowLeft size={16} />}
          type='text'
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
          onClick={handleBackButton}
        />
        <TitleStop text={currentStop ? currentStop.title : `${t('stop')} ${numberNextStop}`} />
      </div>

      <ContainerScroll>
        <ContainerSingleSelect>
          <TitleStop text={t('store')} style={{ fontSize: '12px' }} />
          <SingleSelect
            buttonText={
              currentStore
                ? currentStore.location.name
                : t('jobs.job.info.items-view.add-items-view.catalog.select-store')
            }
            searchText={t('search')}
            isPaginated={true}
            onPageChange={onPageChange}
            onSearch={onSearchStore}
            pageSize={51}
            numberOfPages={numberOfPages}
            onChangeSelected={onChangeSelectedStore}
            dataSource={storesList.map(({ id, location: { name } }) => ({
              value: id,
              label: name,
              disabled: localStops.filter((stop) => stop.id === id).length > 0
            }))}
            dataSelected={currentStore}
            showArrowDown={true}
            disabled={storesList.length === 0 || currentStop}
          />
        </ContainerSingleSelect>

        <ContainerSingleSelect>
          <TitleStop text={t('jobs.job.info.items-view.issue-view.products')} style={{ fontSize: '12px' }} />
          <Select
            placeholder={t('select-products')}
            open={openSelectProducts}
            onDropdownVisibleChange={setOpenSelectProducts}
            dropdownRender={() => (
              <ContainerScrollProducts>
                <ListProducts products={products} hanleSelect={handleAddProduct} />
              </ContainerScrollProducts>
            )}
          />
        </ContainerSingleSelect>

        {productsSelected.length > 0 && (
          <div style={{ margin: '0px 16px' }}>
            {currentStop ? (
              <ListProductsWithDropdown
                products={productsSelected}
                optionsDropdown={getStoresDropdownChangeStop()}
                hanleMoveProduct={confirmMoveProduct}
                hanleDelete={handleDeleteProduct}
              />
            ) : (
              <ListProducts products={productsSelected} hanleDelete={handleDeleteProduct} />
            )}
          </div>
        )}
      </ContainerScroll>
      {!currentStop && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button key='back' type='default' onClick={hanleCancel} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button
            key='submit'
            type='primary'
            onClick={createStop}
            loading={loading}
            disabled={!currentStore || productsSelected.length === 0}
          >
            {t('save')}
          </Button>
        </div>
      )}
    </CardWrapper>
  )
}

export default FormStop
